/** Institution usage (STORY-100, FR-038): what an admin shows to justify the licence.
 *
 * One card inside the institution panel: a month range, a bar per month (answers, with the
 * correct share as the filled part), the totals, the current mastery bands, a per-course table
 * and a CSV. Everything comes aggregated from the analytics service; a course the viewer does
 * not own is an opaque id, and a small course's person counts are withheld before they reach
 * this screen, so there is nothing here to hide. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../api/client";
import { Button, SaveNotice, useFileSave } from "../ui";
import { defaultRange, getCourseUsage, getInstitutionUsage, monthLabel, pct, refreshInstitutionUsage, seriesMax, usageCsv, type CourseUsageRow } from "./usageApi";

const card = "rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm";

export function InstitutionUsage({ id }: { id: string }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [range, setRange] = useState(() => defaultRange(new Date()));
  const valid = range.from <= range.to;
  const key = ["usage", id, range.from, range.to];
  const usage = useQuery({ queryKey: key, queryFn: () => getInstitutionUsage(id, range.from, range.to), enabled: valid, retry: false });
  const courses = useQuery({ queryKey: [...key, "courses"], queryFn: () => getCourseUsage(id, range.from, range.to), enabled: valid && !!usage.data, retry: false });
  const [recounted, setRecounted] = useState(false);
  const recount = useMutation({
    mutationFn: () => refreshInstitutionUsage(id, range.from, range.to),
    onSuccess: () => { setRecounted(true); void qc.invalidateQueries({ queryKey: ["usage", id] }); },
  });
  const { save, result } = useFileSave();
  const n = (v: number) => v.toLocaleString(i18n.language);
  const lang = i18n.language;
  const pick = (k: "from" | "to") => (e: ChangeEvent<HTMLInputElement>) => {
    setRecounted(false);
    if (e.target.value) setRange((r) => ({ ...r, [k]: e.target.value }));
  };

  const data = usage.data;
  const months = data?.months ?? [];
  const max = seriesMax(months.map((m) => m.answers));
  const empty = data !== undefined && data.totals.sessions === 0 && data.totals.answers === 0;
  const forbidden = usage.error instanceof ApiError && usage.error.status === 403;
  // 404 is the contract's answer for an id that is not an institution (the 'personal'
  // sentinel): a fact, not a failure.
  const notInstitution = usage.error instanceof ApiError && usage.error.status === 404;

  return (
    <section className={`${card} flex flex-col gap-3 text-sm`} aria-labelledby={`usage-${id}`}>
      <h3 id={`usage-${id}`} className="font-semibold">{t("usage.title")}</h3>
      <p className="measure text-xs text-[color:var(--ink-2)]">{t("usage.lede")}</p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs">{t("usage.from")}<input className="field field-sm" type="month" value={range.from} max={range.to} onChange={pick("from")} /></label>
        <label className="flex flex-col gap-1 text-xs">{t("usage.to")}<input className="field field-sm" type="month" value={range.to} min={range.from} onChange={pick("to")} /></label>
        {data && !empty && (
          <>
            <Button variant="secondary" size="sm" loading={recount.isPending} onClick={() => recount.mutate()}>{t("usage.refresh")}</Button>
            <Button variant="secondary" size="sm" onClick={() => void save(`usage-${id}-${range.from}-${range.to}.csv`, new Blob([usageCsv(months)], { type: "text/csv" }))}>{t("usage.download")}</Button>
            {recounted && <span className="text-[color:var(--secure)]" role="status">{t("usage.refreshed")}</span>}
          </>
        )}
      </div>
      <SaveNotice result={result} />
      {!valid && <p role="alert" style={{ color: "var(--danger)" }}>{t("usage.badRange")}</p>}
      {usage.isPending && valid && <p className="text-[color:var(--ink-2)]" aria-live="polite">{t("usage.loading")}</p>}
      {notInstitution && <p className="text-[color:var(--ink-2)]">{t("usage.personal")}</p>}
      {usage.isError && !notInstitution && <p role="alert" style={{ color: "var(--danger)" }}>{forbidden ? t("usage.noReach") : t("usage.failed")}</p>}
      {empty && <p className="text-[color:var(--ink-2)]">{t("usage.empty")}</p>}
      {data && !empty && (
        <>
          <ul className="flex flex-col gap-1.5" aria-label={t("usage.barsLabel")}>
            {months.map((m) => {
              const correct = pct(m.correct_share);
              const width = m.answers ? `${Math.max(2, (100 * m.answers) / max)}%` : "0.2rem";
              return (
                <li key={m.month} className="grid grid-cols-[6rem_1fr] items-center gap-x-3 gap-y-0.5 sm:grid-cols-[6rem_1fr_auto]">
                  <span className="num text-xs">{monthLabel(m.month, lang)}</span>
                  <div className="h-5 overflow-hidden rounded-sm" aria-hidden>
                    <div className="relative h-full rounded-sm bg-[color:var(--surface-2)] transition-[width] duration-250 ease-out motion-reduce:transition-none" style={{ width }}>
                      {correct != null && <div className="h-full rounded-sm bg-[color:var(--secure)]" style={{ width: `${correct}%` }} />}
                    </div>
                  </div>
                  <span className="num col-start-2 text-xs text-[color:var(--ink-2)] sm:col-start-3">
                    {t("usage.nAnswers", { count: m.answers })} · {t("usage.nStudents", { count: m.students })} · {t("usage.nSessions", { count: m.sessions })}
                    {correct != null && <> · <span className="text-[color:var(--secure)]">{t("usage.correctShare", { pct: correct })}</span></>}
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="num font-medium">
            {[
              t("usage.nSessions", { count: data.totals.sessions }),
              t("usage.nStudents", { count: data.totals.students }),
              t("usage.nAnswers", { count: data.totals.answers }),
              data.totals.answers_per_student != null ? t("usage.perStudent", { n: n(data.totals.answers_per_student) }) : null,
              pct(data.totals.correct_share) != null ? t("usage.correctShare", { pct: pct(data.totals.correct_share) }) : null,
            ].filter(Boolean).join(" · ")}
          </p>
          <p className="text-xs text-[color:var(--ink-2)]">
            {t("usage.bands", { secure: n(data.bands.secure), developing: n(data.bands.developing), notYet: n(data.bands.not_yet), cells: n(data.bands.cells) })} {t("usage.bandsNote")}
          </p>
          {courses.data && courses.data.courses.length > 0 && (
            <table className="w-full border-collapse text-xs">
              <caption className="mb-1 text-left font-semibold text-[color:var(--ink)]">{t("usage.courses")}</caption>
              <thead>
                <tr className="text-left text-[color:var(--ink-2)]">
                  <th scope="col" className="py-1 pr-2 font-normal">{t("usage.course")}</th>
                  <th scope="col" className="num py-1 pr-2 text-right font-normal">{t("usage.sessions")}</th>
                  <th scope="col" className="num py-1 pr-2 text-right font-normal">{t("usage.students")}</th>
                  <th scope="col" className="num py-1 pr-2 text-right font-normal">{t("usage.answers")}</th>
                  <th scope="col" className="num py-1 text-right font-normal">{t("usage.correct")}</th>
                </tr>
              </thead>
              <tbody>
                {courses.data.courses.map((c) => <CourseRow key={c.course_id} row={c} minCohort={courses.data.min_cohort} />)}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}

function CourseRow({ row, minCohort }: { row: CourseUsageRow; minCohort: number }) {
  const { t, i18n } = useTranslation();
  const n = (v: number | null) => (v == null ? t("usage.none") : v.toLocaleString(i18n.language));
  const correct = pct(row.correct_share);
  return (
    <tr className="border-t border-[color:var(--line)]">
      <td className="py-1 pr-2">
        {row.owned && row.title ? (
          <Link to="/t/c/$courseId/dashboard" params={{ courseId: row.course_id }} className="underline">{row.title}</Link>
        ) : (
          <span className="text-[color:var(--ink-2)]">{t("usage.hiddenTitle")} <code className="font-mono">{row.course_id.slice(0, 8)}</code></span>
        )}
        {row.suppressed && <span className="ml-2 text-[color:var(--ink-2)]">{t("usage.suppressed", { n: minCohort })}</span>}
      </td>
      <td className="num py-1 pr-2 text-right">{n(row.sessions)}</td>
      <td className="num py-1 pr-2 text-right">{n(row.students)}</td>
      <td className="num py-1 pr-2 text-right">{n(row.answers)}</td>
      <td className="num py-1 text-right">{correct == null ? t("usage.none") : t("usage.correctShare", { pct: correct })}</td>
    </tr>
  );
}
